import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { getCroppedFile } from '../lib/cropImage';

const RATIO_PRESETS = [
  { label: 'Square', value: 1 },
  { label: 'Portrait', value: 3 / 4 },
  { label: 'Landscape', value: 4 / 3 },
  { label: 'Wide', value: 16 / 9 },
];

/**
 * Interactive crop window: drag the photo, zoom, and only what stays inside
 * the frame is uploaded.
 */
export function ImageCropper({
  imageSrc,
  fileName = 'image.jpg',
  aspect = 1,
  cropShape = 'rect',
  allowRatioChange = false,
  ratioOptions = RATIO_PRESETS,
  busy = false,
  onCancel,
  onCropped,
  title = 'Position your image',
}) {
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [activeAspect, setActiveAspect] = useState(aspect);
  const [areaPixels, setAreaPixels] = useState(null);
  const [working, setWorking] = useState(false);

  const handleCropComplete = useCallback((_area, croppedAreaPixels) => {
    setAreaPixels(croppedAreaPixels);
  }, []);

  const apply = async () => {
    if (!areaPixels) return;
    setWorking(true);
    try {
      const file = await getCroppedFile(imageSrc, areaPixels, { fileName });
      await onCropped(file);
    } finally {
      setWorking(false);
    }
  };

  const disabled = busy || working;

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-[#0b1b33]/80 backdrop-blur-sm px-4 py-6" data-testid="image-cropper-modal">
      <div className="w-full max-w-lg rounded-2xl bg-white shadow-2xl overflow-hidden">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div>
            <h3 className="text-base font-semibold text-[#0b1b33]">{title}</h3>
            <p className="text-xs text-gray-500">Drag to move • pinch or slide to zoom</p>
          </div>
          <button
            type="button"
            onClick={onCancel}
            disabled={disabled}
            className="text-gray-400 hover:text-gray-700 text-lg leading-none"
            data-testid="cropper-close-button"
          >
            ✕
          </button>
        </div>

        <div className="relative h-72 sm:h-80 bg-[#0b1b33]">
          <Cropper
            image={imageSrc}
            crop={crop}
            zoom={zoom}
            aspect={activeAspect || 1}
            cropShape={cropShape}
            showGrid={cropShape !== 'round'}
            restrictPosition={false}
            minZoom={0.5}
            maxZoom={4}
            onCropChange={setCrop}
            onZoomChange={setZoom}
            onCropComplete={handleCropComplete}
          />
        </div>

        <div className="px-5 py-4 space-y-4">
          <div>
            <label className="flex items-center justify-between text-xs font-medium text-gray-600 mb-1">
              <span>Zoom</span>
              <span>{Math.round(zoom * 100)}%</span>
            </label>
            <input
              type="range"
              min={0.5}
              max={4}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full accent-[#c9a227]"
              data-testid="cropper-zoom-slider"
            />
          </div>

          {allowRatioChange && (
            <div className="flex flex-wrap gap-2">
              {ratioOptions.map((option) => (
                <button
                  key={option.label}
                  type="button"
                  onClick={() => setActiveAspect(option.value)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                    Math.abs((activeAspect || 1) - option.value) < 0.001
                      ? 'bg-[#0b1b33] text-white border-[#0b1b33]'
                      : 'bg-white text-gray-600 border-gray-200 hover:border-[#c9a227]'
                  }`}
                  data-testid={`cropper-ratio-${option.label.toLowerCase()}`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          )}

          <div className="flex gap-3">
            <button
              type="button"
              onClick={apply}
              disabled={disabled || !areaPixels}
              className="flex-1 py-2.5 rounded-lg bg-[#c9a227] text-[#0b1b33] font-semibold hover:bg-[#b8931f] disabled:opacity-50"
              data-testid="cropper-apply-button"
            >
              {disabled ? 'Saving...' : 'Crop & Upload'}
            </button>
            <button
              type="button"
              onClick={onCancel}
              disabled={disabled}
              className="px-5 py-2.5 rounded-lg bg-gray-100 text-gray-700 font-medium hover:bg-gray-200 disabled:opacity-50"
              data-testid="cropper-cancel-button"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default ImageCropper;
